// =============================================================================
// MfaSettingsSection.jsx — KlassDoc Sprint 10C Session D2
// -----------------------------------------------------------------------------
// Lives inside the User Settings modal. Handles the full lifecycle:
//   status read → enroll (QR + verify) → backup-code display → disable →
//   regenerate backup codes.
//
// Self-contained: the parent only needs to pass api, lang, THEME, and
// a toast helper. Polls /auth/mfa/status on mount and after every
// mutation so the parent doesn't have to coordinate state.
// =============================================================================
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }
function T(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function MfaSettingsSection({ api, lang, THEME, toast }) {
  const [status, setStatus] = useState(null); // { enabled, enrolled_at, remaining_backup_codes }
  const [loading, setLoading] = useState(true);

  // Enrollment flow
  const [enrollPhase, setEnrollPhase] = useState(null); // null | 'qr' | 'codes'
  const [enrollData, setEnrollData] = useState(null); // { qr_code, manual_entry_key, ... }
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [enrollError, setEnrollError] = useState(null);

  // Disable flow
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  // Regenerate flow
  const [showRegen, setShowRegen] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  async function loadStatus() {
    setLoading(true);
    try {
      const s = await api.get("/auth/mfa/status");
      setStatus(s);
    } catch (e) {
      setStatus({ enabled: false });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  async function handleStartEnroll() {
    setEnrollError(null);
    try {
      const data = await api.post("/auth/mfa/enroll/start", {});
      setEnrollData(data);
      setEnrollPhase("qr");
      setVerifyCode("");
    } catch (e) {
      toast(e?.message || fmt(lang, "Échec du démarrage", "Failed to start enrollment"), "error");
    }
  }

  async function handleVerifyEnroll(e) {
    e?.preventDefault();
    if (!verifyCode.trim() || verifying) return;
    setVerifying(true); setEnrollError(null);
    try {
      const res = await api.post("/auth/mfa/enroll/verify", { code: verifyCode.trim() });
      setBackupCodes(res.backup_codes || []);
      setEnrollPhase("codes");
      toast(fmt(lang, "Double authentification activée", "Two-factor authentication enabled"), "success");
      await loadStatus();
    } catch (e) {
      setEnrollError(e?.message || fmt(lang, "Code invalide", "Invalid code"));
    } finally { setVerifying(false); }
  }

  function handleCloseEnroll() {
    setEnrollPhase(null); setEnrollData(null); setVerifyCode(""); setBackupCodes(null); setEnrollError(null);
  }

  async function handleDisable(e) {
    e?.preventDefault();
    if (!disablePassword || !disableCode || disabling) return;
    setDisabling(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePassword, code: disableCode.trim() });
      toast(fmt(lang, "Double authentification désactivée", "Two-factor authentication disabled"), "success");
      setShowDisable(false); setDisablePassword(""); setDisableCode("");
      await loadStatus();
    } catch (e) {
      toast(e?.message || fmt(lang, "Échec de la désactivation", "Failed to disable"), "error");
    } finally { setDisabling(false); }
  }

  async function handleRegenerate(e) {
    e?.preventDefault();
    if (!regenPassword || !regenCode || regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/auth/mfa/backup-codes/regenerate", { password: regenPassword, code: regenCode.trim() });
      setBackupCodes(res.backup_codes || []);
      setEnrollPhase("codes"); // Reuse the "codes" phase UI to display
      setShowRegen(false); setRegenPassword(""); setRegenCode("");
      toast(fmt(lang, "Codes régénérés", "Codes regenerated"), "success");
      await loadStatus();
    } catch (e) {
      toast(e?.message || fmt(lang, "Échec de la régénération", "Failed to regenerate"), "error");
    } finally { setRegenerating(false); }
  }

  function downloadCodes() {
    if (!backupCodes || !backupCodes.length) return;
    const header = `KlassDoc — ${fmt(lang, "Codes de secours", "Backup codes")}\n${fmt(lang, "Généré le", "Generated on")}: ${new Date().toLocaleString()}\n\n${fmt(lang, "Chaque code ne fonctionne qu'une seule fois.", "Each code works only once.")}\n\n`;
    const body = backupCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}. ${c}`).join("\n");
    const blob = new Blob([header + body + "\n"], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `klassdoc-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }

  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";

  // ── Render ──
  if (loading) {
    return (
      <div style={{ padding: "16px 0", display: "flex", alignItems: "center", gap: 10, color: THEME.textDim, fontSize: 13 }}>
        <lucide.Loader2 size={14} className="spin" />
        {fmt(lang, "Chargement...", "Loading...")}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ height: 1, background: THEME.borderSubtle, marginBottom: 18 }} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <lucide.ShieldCheck size={14} color={status?.enabled ? THEME.success : THEME.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: THEME.text }}>
              {T(lang, "Double authentification", "Two-factor authentication")}
            </span>
            {status?.enabled && (
              <span style={{ padding: "2px 8px", borderRadius: 4, background: THEME.successDim, color: THEME.success, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
                {T(lang, "Actif", "Active")}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: THEME.textDim, marginTop: 4, lineHeight: 1.5 }}>
            {status?.enabled
              ? (<>
                {T(lang, "Activée le ", "Enabled on ")}
                <strong style={{ color: THEME.textMuted }}>{status.enrolled_at ? new Date(status.enrolled_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US") : ""}</strong>
                {" · "}
                <span style={{ color: status.remaining_backup_codes <= 2 ? THEME.warning : THEME.textMuted }}>
                  {status.remaining_backup_codes || 0} {T(lang, "codes de secours restants", "backup codes remaining")}
                </span>
              </>)
              : T(lang, "Protégez votre compte en exigeant un code temporaire à chaque connexion.", "Protect your account by requiring a one-time code on every sign-in.")}
          </div>
        </div>
        {!status?.enabled && (
          <button onClick={handleStartEnroll} style={{ padding: "8px 14px", borderRadius: 8, background: THEME.accent, color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
            <lucide.Shield size={12} style={{ marginRight: 6, verticalAlign: -2 }} />
            {T(lang, "Activer", "Enable")}
          </button>
        )}
        {status?.enabled && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button onClick={() => setShowRegen(true)} style={{ padding: "6px 12px", borderRadius: 6, background: "transparent", color: THEME.info, border: `1px solid ${THEME.info}40`, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
              <lucide.RefreshCw size={11} style={{ marginRight: 5, verticalAlign: -2 }} />
              {T(lang, "Régénérer les codes", "Regenerate codes")}
            </button>
            <button onClick={() => setShowDisable(true)} style={{ padding: "6px 12px", borderRadius: 6, background: "transparent", color: THEME.danger, border: `1px solid ${THEME.danger}40`, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
              {T(lang, "Désactiver", "Disable")}
            </button>
          </div>
        )}
      </div>

      {/* Enrollment modal */}
      <AnimatePresence>
        {enrollPhase && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2200, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
            onClick={e => { if (e.target === e.currentTarget && !verifying) handleCloseEnroll(); }}
          >
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
              style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 14, width: "100%", maxWidth: 460, maxHeight: "88vh", overflow: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}
            >
              {enrollPhase === "qr" && enrollData && (
                <div style={{ padding: 28 }}>
                  <div style={{ textAlign: "center", marginBottom: 18 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: 12, background: `${THEME.accent}18`, marginBottom: 12 }}>
                      <lucide.ShieldCheck size={24} color={THEME.accent} />
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.text, margin: "0 0 6px 0" }}>
                      {T(lang, "Scannez le QR code", "Scan the QR code")}
                    </h3>
                    <p style={{ fontSize: 12, color: THEME.textDim, margin: 0, lineHeight: 1.55 }}>
                      {T(lang, "Utilisez Google Authenticator, Authy, 1Password ou toute autre app compatible.", "Use Google Authenticator, Authy, 1Password, or any compatible app.")}
                    </p>
                  </div>

                  <div style={{ background: "#fff", borderRadius: 10, padding: 16, display: "flex", justifyContent: "center", marginBottom: 14 }}>
                    <img src={enrollData.qr_code} alt="TOTP QR code" style={{ width: 220, height: 220 }} />
                  </div>

                  <div style={{ padding: 12, background: THEME.surface, borderRadius: 8, border: `1px solid ${THEME.borderSubtle}`, marginBottom: 16 }}>
                    <div style={{ fontSize: 10, color: THEME.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                      {T(lang, "Ou saisissez cette clé manuellement", "Or enter this key manually")}
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono',Consolas,monospace", fontSize: 12, color: THEME.text, wordBreak: "break-all", userSelect: "all" }}>
                      {enrollData.manual_entry_key}
                    </div>
                  </div>

                  <form onSubmit={handleVerifyEnroll}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }}>
                      {T(lang, "Entrez le code à 6 chiffres", "Enter the 6-digit code")}
                    </label>
                    <input
                      type="text" inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code"
                      value={verifyCode} onChange={e => setVerifyCode(e.target.value)}
                      placeholder="000000"
                      style={{ width: "100%", padding: "12px 14px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 18, textAlign: "center", letterSpacing: 4, fontFamily: "'JetBrains Mono',Consolas,monospace", outline: "none" }}
                    />
                    {enrollError && (
                      <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: THEME.dangerDim, color: THEME.danger, fontSize: 12 }}>
                        {enrollError}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                      <button type="button" onClick={handleCloseEnroll} disabled={verifying} style={{ flex: 1, padding: "10px", borderRadius: 8, background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>
                        {T(lang, "Annuler", "Cancel")}
                      </button>
                      <button type="submit" disabled={verifying || !verifyCode.trim()} style={{ flex: 1, padding: "10px", borderRadius: 8, background: THEME.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: (verifying || !verifyCode.trim()) ? "not-allowed" : "pointer", fontFamily: FONT, opacity: (verifying || !verifyCode.trim()) ? 0.6 : 1 }}>
                        {verifying ? <lucide.Loader2 size={14} className="spin" /> : T(lang, "Vérifier", "Verify")}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {enrollPhase === "codes" && backupCodes && (
                <div style={{ padding: 28 }}>
                  <div style={{ textAlign: "center", marginBottom: 16 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: 12, background: `${THEME.warning}18`, marginBottom: 12 }}>
                      <lucide.Key size={24} color={THEME.warning} />
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.text, margin: "0 0 6px 0" }}>
                      {T(lang, "Sauvegardez ces codes de secours", "Save these backup codes")}
                    </h3>
                    <p style={{ fontSize: 12, color: THEME.textDim, margin: 0, lineHeight: 1.55 }}>
                      {T(lang, "Chaque code ne peut être utilisé qu'une seule fois. Gardez-les en lieu sûr.", "Each code works only once. Keep them somewhere safe.")}
                    </p>
                  </div>

                  <div style={{ padding: 16, background: THEME.surface, border: `1px solid ${THEME.warning}30`, borderRadius: 10, marginBottom: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontFamily: "'JetBrains Mono',Consolas,monospace", fontSize: 13, color: THEME.text, userSelect: "all" }}>
                      {backupCodes.map((c, i) => (
                        <div key={i} style={{ padding: "6px 10px", background: THEME.surfaceRaised, borderRadius: 6, border: `1px solid ${THEME.borderSubtle}` }}>
                          <span style={{ color: THEME.textDim, fontSize: 10, marginRight: 6 }}>{String(i + 1).padStart(2, "0")}</span>
                          {c}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={downloadCodes} style={{ flex: 1, padding: "10px", borderRadius: 8, background: THEME.surface, color: THEME.info, border: `1px solid ${THEME.info}40`, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>
                      <lucide.Download size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                      {T(lang, "Télécharger", "Download")}
                    </button>
                    <button onClick={handleCloseEnroll} style={{ flex: 1, padding: "10px", borderRadius: 8, background: THEME.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
                      <lucide.Check size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
                      {T(lang, "J'ai sauvegardé mes codes", "I've saved my codes")}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disable modal */}
      <AnimatePresence>
        {showDisable && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2200, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
            onClick={e => { if (e.target === e.currentTarget && !disabling) { setShowDisable(false); setDisablePassword(""); setDisableCode(""); } }}
          >
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 14, width: "100%", maxWidth: 400, padding: 28, boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 12, background: `${THEME.danger}18`, marginBottom: 10 }}>
                  <lucide.ShieldOff size={22} color={THEME.danger} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: THEME.text, margin: "0 0 6px 0" }}>
                  {T(lang, "Désactiver la double authentification", "Disable two-factor authentication")}
                </h3>
                <p style={{ fontSize: 11, color: THEME.textDim, margin: 0, lineHeight: 1.5 }}>
                  {T(lang, "Votre compte sera moins protégé.", "Your account will be less secure.")}
                </p>
              </div>
              <form onSubmit={handleDisable} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input type="password" placeholder={T(lang, "Mot de passe actuel", "Current password")} value={disablePassword} onChange={e => setDisablePassword(e.target.value)} style={{ padding: "10px 12px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 13, outline: "none", fontFamily: FONT }} autoFocus />
                <input type="text" inputMode="numeric" maxLength={6} placeholder={T(lang, "Code à 6 chiffres", "6-digit code")} value={disableCode} onChange={e => setDisableCode(e.target.value)} style={{ padding: "10px 12px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 14, textAlign: "center", letterSpacing: 3, fontFamily: "'JetBrains Mono',Consolas,monospace", outline: "none" }} />
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => { setShowDisable(false); setDisablePassword(""); setDisableCode(""); }} disabled={disabling} style={{ flex: 1, padding: "10px", borderRadius: 8, background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>
                    {T(lang, "Annuler", "Cancel")}
                  </button>
                  <button type="submit" disabled={disabling || !disablePassword || !disableCode} style={{ flex: 1, padding: "10px", borderRadius: 8, background: THEME.danger, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: (disabling || !disablePassword || !disableCode) ? "not-allowed" : "pointer", fontFamily: FONT, opacity: (disabling || !disablePassword || !disableCode) ? 0.6 : 1 }}>
                    {disabling ? <lucide.Loader2 size={14} className="spin" /> : T(lang, "Désactiver", "Disable")}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regenerate modal */}
      <AnimatePresence>
        {showRegen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 2200, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
            onClick={e => { if (e.target === e.currentTarget && !regenerating) { setShowRegen(false); setRegenPassword(""); setRegenCode(""); } }}
          >
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 14, width: "100%", maxWidth: 400, padding: 28, boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 12, background: `${THEME.info}18`, marginBottom: 10 }}>
                  <lucide.RefreshCw size={22} color={THEME.info} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: THEME.text, margin: "0 0 6px 0" }}>
                  {T(lang, "Régénérer les codes de secours", "Regenerate backup codes")}
                </h3>
                <p style={{ fontSize: 11, color: THEME.textDim, margin: 0, lineHeight: 1.5 }}>
                  {T(lang, "Les anciens codes deviendront invalides.", "Your old codes will be invalidated.")}
                </p>
              </div>
              <form onSubmit={handleRegenerate} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input type="password" placeholder={T(lang, "Mot de passe actuel", "Current password")} value={regenPassword} onChange={e => setRegenPassword(e.target.value)} style={{ padding: "10px 12px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 13, outline: "none", fontFamily: FONT }} autoFocus />
                <input type="text" inputMode="numeric" maxLength={6} placeholder={T(lang, "Code à 6 chiffres", "6-digit code")} value={regenCode} onChange={e => setRegenCode(e.target.value)} style={{ padding: "10px 12px", background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 8, color: THEME.text, fontSize: 14, textAlign: "center", letterSpacing: 3, fontFamily: "'JetBrains Mono',Consolas,monospace", outline: "none" }} />
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => { setShowRegen(false); setRegenPassword(""); setRegenCode(""); }} disabled={regenerating} style={{ flex: 1, padding: "10px", borderRadius: 8, background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>
                    {T(lang, "Annuler", "Cancel")}
                  </button>
                  <button type="submit" disabled={regenerating || !regenPassword || !regenCode} style={{ flex: 1, padding: "10px", borderRadius: 8, background: THEME.info, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: (regenerating || !regenPassword || !regenCode) ? "not-allowed" : "pointer", fontFamily: FONT, opacity: (regenerating || !regenPassword || !regenCode) ? 0.6 : 1 }}>
                    {regenerating ? <lucide.Loader2 size={14} className="spin" /> : T(lang, "Régénérer", "Regenerate")}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
